import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  AlertTitle,
  LinearProgress,
} from '@mui/material';
import {
  Assignment,
  Add,
  Edit,
  Delete,
  Send,
  Download,
  Visibility,
  AttachMoney,
  Schedule,
  Business,
  CheckCircle,
  Pending,
  Cancel,
  TrendingUp,
} from '@mui/icons-material';
import { useBrandColors } from '../hooks/useBrandColors';
import timezoneService from '../services/timezoneService';

interface Estimate {
  id: string;
  estimateNumber: string;
  clientName: string;
  projectName: string;
  amount: number;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
  createdDate: string;
  validUntil: string;
  description: string;
  estimatedHours: number;
}

export const Estimates: React.FC = () => {
  const brandColors = useBrandColors();
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [loading, setLoading] = useState(false);

  // Mock data for demonstration
  useEffect(() => {
    const mockEstimates: Estimate[] = [
      {
        id: '1',
        estimateNumber: 'EST-2025-001',
        clientName: 'Acme Corporation',
        projectName: 'E-commerce Platform',
        amount: 15000.00,
        status: 'sent',
        createdDate: '2025-07-20',
        validUntil: '2025-08-20',
        description: 'Complete e-commerce platform with payment integration',
        estimatedHours: 120
      },
      {
        id: '2',
        estimateNumber: 'EST-2025-002',
        clientName: 'Tech Solutions Inc',
        projectName: 'Mobile App Development',
        amount: 8500.00,
        status: 'accepted',
        createdDate: '2025-07-10',
        validUntil: '2025-08-10',
        description: 'Cross-platform mobile application development',
        estimatedHours: 80
      },
      {
        id: '3',
        estimateNumber: 'EST-2025-003',
        clientName: 'StartupXYZ',
        projectName: 'Website Redesign',
        amount: 3200.00,
        status: 'draft',
        createdDate: '2025-07-25',
        validUntil: '2025-08-25',
        description: 'Complete website redesign with modern UI/UX',
        estimatedHours: 40
      },
      {
        id: '4',
        estimateNumber: 'EST-2025-004',
        clientName: 'Local Business',
        projectName: 'Inventory System',
        amount: 5800.00,
        status: 'rejected',
        createdDate: '2025-07-05',
        validUntil: '2025-08-05',
        description: 'Custom inventory management system',
        estimatedHours: 60
      }
    ];
    setEstimates(mockEstimates);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'sent': return 'info';
      case 'rejected': return 'error';
      case 'expired': return 'warning';
      case 'draft': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'accepted': return <CheckCircle />;
      case 'sent': return <Send />;
      case 'rejected': return <Cancel />;
      case 'expired': return <Schedule />;
      case 'draft': return <Pending />;
      default: return <Pending />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    // Convert UTC date string to user timezone and format
    const userDate = timezoneService.convertUTCToUserTime(dateString);
    return timezoneService.formatUserDate(userDate);
  };

  const totalAmount = estimates.reduce((sum, estimate) => sum + estimate.amount, 0);
  const acceptedAmount = estimates
    .filter(estimate => estimate.status === 'accepted')
    .reduce((sum, estimate) => sum + estimate.amount, 0);
  const pendingAmount = estimates
    .filter(estimate => estimate.status === 'sent')
    .reduce((sum, estimate) => sum + estimate.amount, 0);
  const acceptanceRate = estimates.length > 0 
    ? (estimates.filter(e => e.status === 'accepted').length / estimates.length) * 100 
    : 0;

  const styles = {
    container: {
      backgroundColor: 'white',
      minHeight: '100vh',
      padding: '24px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '24px'
    },
    summaryGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '32px'
    },
    summaryCard: {
      textAlign: 'center',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
    },
    summaryValue: {
      fontSize: '24px',
      fontWeight: '700',
      marginBottom: '8px'
    },
    summaryLabel: {
      fontSize: '14px',
      color: '#666'
    },
    actionButton: {
      marginBottom: '24px'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <Assignment sx={{ fontSize: '32px', color: brandColors.primary }} />
        <h1 style={{ ...styles.title, color: brandColors.textPrimary }}>Estimates</h1>
      </div>
      
      <p style={styles.subtitle}>
        Create and manage project estimates, track client responses, and convert accepted estimates to projects.
      </p>

      {/* Summary Cards */}
      <div style={styles.summaryGrid}>
        <Card sx={styles.summaryCard}>
          <CardContent>
            <AttachMoney sx={{ fontSize: 32, color: brandColors.primary, mb: 1 }} />
            <Typography sx={{ ...styles.summaryValue, color: brandColors.primary }}>
              {formatCurrency(totalAmount)}
            </Typography>
            <Typography sx={styles.summaryLabel}>Total Estimated</Typography>
          </CardContent>
        </Card>

        <Card sx={styles.summaryCard}>
          <CardContent>
            <CheckCircle sx={{ fontSize: 32, color: brandColors.secondary, mb: 1 }} />
            <Typography sx={{ ...styles.summaryValue, color: brandColors.secondary }}>
              {formatCurrency(acceptedAmount)}
            </Typography>
            <Typography sx={styles.summaryLabel}>Accepted Value</Typography>
          </CardContent>
        </Card>

        <Card sx={styles.summaryCard}>
          <CardContent>
            <Schedule sx={{ fontSize: 32, color: brandColors.accent, mb: 1 }} />
            <Typography sx={{ ...styles.summaryValue, color: brandColors.accent }}>
              {formatCurrency(pendingAmount)}
            </Typography>
            <Typography sx={styles.summaryLabel}>Pending Response</Typography>
          </CardContent>
        </Card>

        <Card sx={styles.summaryCard}>
          <CardContent>
            <TrendingUp sx={{ fontSize: 32, color: brandColors.primary, mb: 1 }} />
            <Typography sx={{ ...styles.summaryValue, color: brandColors.primary }}>
              {acceptanceRate.toFixed(1)}%
            </Typography>
            <Typography sx={styles.summaryLabel}>Acceptance Rate</Typography>
          </CardContent>
        </Card>

        <Card sx={styles.summaryCard}>
          <CardContent>
            <Assignment sx={{ fontSize: 32, color: brandColors.secondary, mb: 1 }} />
            <Typography sx={{ ...styles.summaryValue, color: brandColors.secondary }}>
              {estimates.length}
            </Typography>
            <Typography sx={styles.summaryLabel}>Total Estimates</Typography>
          </CardContent>
        </Card>
      </div>

      {/* Action Button */}
      <Button
        variant="contained"
        startIcon={<Add />}
        sx={{
          ...styles.actionButton,
          backgroundColor: brandColors.primary,
          '&:hover': { backgroundColor: brandColors.secondary }
        }}
        onClick={() => alert('Create Estimate functionality will be implemented')}
      >
        Create Estimate
      </Button>

      {/* Estimates Table */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Assignment />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Estimate List
          </Typography>
          <Chip
            label={`${estimates.length} estimates`}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Estimate #</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Project</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Hours</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Valid Until</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {estimates.map((estimate) => {
                const isExpiringSoon = new Date(estimate.validUntil) < new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
                
                return (
                  <TableRow key={estimate.id}>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        {estimate.estimateNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {estimate.clientName}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {estimate.projectName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {estimate.description.length > 50 
                          ? `${estimate.description.substring(0, 50)}...` 
                          : estimate.description}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(estimate.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {estimate.estimatedHours}h
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                          icon={getStatusIcon(estimate.status)}
                          label={estimate.status.charAt(0).toUpperCase() + estimate.status.slice(1)}
                          color={getStatusColor(estimate.status) as any}
                          size="small"
                        />
                        {isExpiringSoon && estimate.status === 'sent' && (
                          <Typography variant="caption" color="warning.main">
                            Expires soon
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(estimate.createdDate)}</TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        color={isExpiringSoon ? 'warning.main' : 'text.primary'}
                      >
                        {formatDate(estimate.validUntil)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="View Estimate">
                          <IconButton size="small" color="primary">
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Estimate">
                          <IconButton size="small" color="primary">
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton size="small" color="primary">
                            <Download />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Estimate">
                          <IconButton size="small" color="error">
                            <Delete />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {estimates.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Assignment sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No estimates yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create your first estimate to start proposing projects to clients.
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Acceptance Rate Progress */}
      <Paper sx={{ p: 3, mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <TrendingUp />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Estimate Performance
          </Typography>
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Acceptance Rate</Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {acceptanceRate.toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={acceptanceRate}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#f0f0f0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: acceptanceRate >= 70 ? '#27ae60' : acceptanceRate >= 40 ? '#f39c12' : '#e74c3c'
              }
            }}
          />
        </Box>

        <Typography variant="caption" color="text.secondary">
          {acceptanceRate >= 70 
            ? 'Excellent acceptance rate! Your estimates are well-received.'
            : acceptanceRate >= 40 
            ? 'Good acceptance rate. Consider reviewing pricing or scope.'
            : 'Low acceptance rate. Review your estimation strategy.'}
        </Typography>
      </Paper>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <AlertTitle>Coming Soon</AlertTitle>
        Full estimate management features including PDF generation, email sending, client approval workflows, and automatic conversion to projects will be implemented in future updates.
      </Alert>
    </div>
  );
};

export default Estimates;
